import clsx from "clsx";
import { CalendarRange, Check, ChevronDown } from "lucide-react";
import { Fragment, useState } from "react";
import { isDay, isPeriod, type Period, PRESET_GROUPS, PRESET_LABELS, type PresetId, periodText, presetPeriod } from "../../shared/periods";
import { useCompany } from "../lib/company";
import { Popover } from "./ui";

/*
 * Choosing a period for stats, as accounting software does (Xero's): this, last and to date, by
 * month, quarter and financial year (the business's: shared/periods.ts), or a custom range. Each
 * shows its days. The choice is remembered on this device for every stats tab (usePeriod).
 */

/** A ready-made period (worked out from today each time it's used), or set days. */
export type PeriodChoice = { preset: PresetId } | { custom: Period };

const STORAGE_KEY = "stats-period";
const DEFAULT: PeriodChoice = { preset: "last12" };

function stored(): PeriodChoice {
    try {
        const c = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as PeriodChoice | null;
        if (c && "preset" in c && c.preset in PRESET_LABELS) return c;
        if (c && "custom" in c && isPeriod(c.custom.from, c.custom.to)) return c;
    } catch {
        // Unreadable, or storage blocked: the default.
    }
    return DEFAULT;
}

/** The chosen period: its days, its name ("This quarter", or the days for a custom one), and a way to choose another. */
export function usePeriod() {
    const company = useCompany();
    const [choice, setChoice] = useState<PeriodChoice>(stored);
    const period = "preset" in choice ? presetPeriod(choice.preset, new Date(), company.financialYearStart, company.timeZone) : choice.custom;
    const name = "preset" in choice ? PRESET_LABELS[choice.preset] : periodText(choice.custom, company.money.locale);
    const choose = (next: PeriodChoice) => {
        setChoice(next);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            // Not remembered: fine.
        }
    };
    return { choice, period, name, choose };
}

/** The button showing the chosen period ("This quarter ▾"), opening the list of periods. */
export function PeriodPicker({ value }: Readonly<{ value: ReturnType<typeof usePeriod> }>) {
    const company = useCompany();
    const today = new Date();
    const chosen = (id: PresetId) => "preset" in value.choice && value.choice.preset === id;
    return (
        <Popover
            label="Choose the period"
            width="w-[22rem]"
            buttonClassName="btn-secondary px-2.5 py-1 text-xs"
            button={
                <>
                    {value.name}
                    <ChevronDown className="size-3.5 text-muted" />
                </>
            }
        >
            {(close) => (
                <div className="max-h-[70vh] overflow-y-auto">
                    {PRESET_GROUPS.map((group, i) => (
                        <Fragment key={group[0]}>
                            {i ? <hr className="my-1 border-line" /> : null}
                            {group.map((id) => (
                                <button
                                    key={id}
                                    type="button"
                                    className={clsx("flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-left hover:bg-surface-2", chosen(id) && "font-semibold")}
                                    onClick={() => {
                                        value.choose({ preset: id });
                                        close();
                                    }}
                                >
                                    <Check className={clsx("size-3.5 shrink-0 text-brand-ink", !chosen(id) && "invisible")} />
                                    <span className="min-w-0 flex-1 truncate">{PRESET_LABELS[id]}</span>
                                    <span className="shrink-0 text-xs font-normal text-muted tabular-nums">{periodText(presetPeriod(id, today, company.financialYearStart, company.timeZone), company.money.locale)}</span>
                                </button>
                            ))}
                        </Fragment>
                    ))}
                    <hr className="my-1 border-line" />
                    <CustomRange
                        start={value.period}
                        active={"custom" in value.choice}
                        onApply={(p) => {
                            value.choose({ custom: p });
                            close();
                        }}
                    />
                </div>
            )}
        </Popover>
    );
}

/** Any days: from and to (both included), starting from the period shown now. */
function CustomRange({ start, active, onApply }: Readonly<{ start: Period; active: boolean; onApply: (p: Period) => void }>) {
    const [from, setFrom] = useState(start.from);
    const [to, setTo] = useState(start.to);
    const valid = isPeriod(from, to);
    // Both days given, but too far apart (the button stays off: say why).
    const tooLong = isDay(from) && isDay(to) && from <= to && !valid;
    return (
        <form
            className="space-y-2 px-3 py-2"
            onSubmit={(e) => {
                e.preventDefault();
                if (valid) onApply({ from, to });
            }}
        >
            <div className={clsx("flex items-center gap-2", active && "font-semibold")}>
                <CalendarRange className="size-3.5 shrink-0 text-muted" />
                Custom date range
            </div>
            <div className="flex items-center gap-2">
                <input type="date" className="input py-1 text-xs" aria-label="From" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                <span className="text-muted">to</span>
                <input type="date" className="input py-1 text-xs" aria-label="To" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="flex items-center justify-end gap-3">
                {tooLong ? <span className="text-xs text-danger">At most 10 years</span> : null}
                <button type="submit" className="btn-primary px-2.5 py-1 text-xs" disabled={!valid}>
                    Show
                </button>
            </div>
        </form>
    );
}
